
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import * as echarts from 'echarts'
import strategyApi from '@/api/strategy'
import { zh_tran } from '@/utils/lang.js'
import Nodata from '@/components/page/nodata.vue'

// timePeriod 映射：tab value → 接口参数
const PERIOD_MAP: Record<string, '1' | '2' | '3' | '4'> = {
  '3M':  '1',
  '1Y':  '2',
  'YTD': '3',
  'ALL': '4'
}

/** ECharts 内部 series 标识，展示名通过 tran 转换 */
const SERIES_STRATEGY = 'strategy'
const SERIES_BENCHMARK = 'benchmark'

export default defineComponent({
  name: 'YieldChart',
  components: { Nodata },
  props: {
    market:       { type: String,  default: 'HK' },
    code:         { type: String,  default: '' },
    isTraditional:{ type: Boolean, default: false },
    defaultRange: { type: String,  default: '1Y' }
  },
  setup(props) {
    const route = useRoute()
    // token / 主题 从路由参数读取，无需父组件传入
    const token = (route.query.token as string) || ''
    const isTraditionalFromRoute = (route.query['Accept-Language'] as string) === 'zh-Hant'
    const isTraditionalVal = computed(() => props.isTraditional || isTraditionalFromRoute)
    const isDark = computed(() => route.query.theme !== 'light')
    const preferGreenUp = computed(() => route.query.preferGreenUptrendRedDowntrend === '1')

    // YieldChart 局部主题变量
    const chartVars = computed(() => isDark.value ? ({
      bg:           '#111424',
      headerText:   '#ffffff',
      textSub:      'rgba(255,255,255,0.45)',
      textPri:      'rgba(255,255,255,0.85)',
      border:       'rgba(255,255,255,0.06)',
      benchmarkLine:'rgba(255,255,255,0.6)',
      spinBorder:   'rgba(255,255,255,0.15)',
      spinTop:      'rgba(255,255,255,0.6)',
    }) : ({
      bg:           '#ffffff',
      headerText:   '#1d1d1d',
      textSub:      '#7d7d7d',
      textPri:      '#1d1d1d',
      border:       '#e8e8e8',
      benchmarkLine:'rgba(100,120,160,0.7)',
      spinBorder:   'rgba(0,0,0,0.1)',
      spinTop:      'rgba(0,0,0,0.45)',
    }))
    const chartRef = ref<HTMLElement | null>(null)
    let chartInstance: echarts.ECharts | null = null
    const currentRange = ref(props.defaultRange)
    const loading = ref(false)

    // 文字转换
    function tran(text: string) {
      if (isTraditionalVal.value) return (text as any).tran()
      return text
    }

    function switchLanguage() {
      if (isTraditionalVal.value) nextTick(() => zh_tran('t'))
    }

    // 当前图表数据
    const strategyData = ref<{ date: string; value: number }[]>([])
    const benchmarkData = ref<{ date: string; value: number }[]>([])
    const benchmarkNameFromApi = ref('')

    const timeOptions = computed(() => [
      { label: tran('近3月'),   value: '3M'  },
      { label: tran('近1年'),   value: '1Y'  },
      { label: tran('今年以来'), value: 'YTD' },
      { label: tran('全部'),    value: 'ALL' }
    ])

    const strategySeriesName = computed(() => tran('本策略'))

    const benchmarkName = computed(() => {
      const fallback = props.market === 'HK' ? '恒生指数' : '标普500'
      return tran(benchmarkNameFromApi.value || fallback)
    })

    // tradingDay 可能是 YYYYMMDD 整数 或 毫秒时间戳，统一转为 YYYY-MM-DD
    const formatTradingDay = (day: number): string => {
      const s = String(day)
      // YYYYMMDD 格式（8位纯数字）
      if (s.length === 8 && !s.startsWith('1') || (s.length === 8)) {
        const y = s.slice(0, 4)
        const m = s.slice(4, 6)
        const d = s.slice(6, 8)
        const parsed = new Date(`${y}-${m}-${d}`)
        if (!isNaN(parsed.getTime())) return `${y}-${m}-${d}`
      }
      // 毫秒时间戳（13位）或秒时间戳（10位）
      const ts = s.length <= 10 ? day * 1000 : day
      const date = new Date(ts)
      if (isNaN(date.getTime())) return s
      const y = date.getFullYear()
      const mo = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${mo}-${d}`
    }

    const clearChartData = () => {
      strategyData.value = []
      benchmarkData.value = []
      benchmarkNameFromApi.value = ''
      if (chartInstance) {
        chartInstance.dispose()
        chartInstance = null
      }
    }

    // 拉取接口数据
    const fetchData = async () => {
      if (!props.code || !token) {
        clearChartData()
        loading.value = false
        return
      }
      loading.value = true
      clearChartData()
      try {
        const res: any = await strategyApi.getCumulative({
          token,
          code: props.code,
          timePeriod: PERIOD_MAP[currentRange.value]
        })
        if (res?.data) {
          const d = res.data
          benchmarkNameFromApi.value = d.benchmarkName || ''

          strategyData.value = (d.dayReturns || []).map((item: any) => ({
            date: formatTradingDay(item.tradingDay),
            value: +(item.cumulativeReturn * 100).toFixed(4)
          }))

          benchmarkData.value = (d.benchmarkReturns || []).map((item: any) => ({
            date: formatTradingDay(item.tradingDay),
            value: +(item.cumulativeReturn * 100).toFixed(4)
          }))
        } else {
          clearChartData()
        }
      } catch (e) {
        console.error('[YieldChart] fetchData error', e)
        clearChartData()
      } finally {
        loading.value = false
        if (strategyData.value.length > 0) {
          // 等 v-else 的 chart-container DOM 渲染完成后再初始化图表
          await nextTick()
          await nextTick()
          renderChart()
        }
        switchLanguage()
      }
    }

    const changeRange = (range: string) => {
      currentRange.value = range
      fetchData()
    }

    const renderChart = () => {
      if (!chartRef.value) return
      // v-if 切换后 DOM 是新元素，旧实例需要重建
      if (chartInstance) {
        chartInstance.dispose()
        chartInstance = null
      }
      chartInstance = echarts.init(chartRef.value)

      const dates = strategyData.value.map(p => p.date)
      const strategyValues = strategyData.value.map(p => p.value)
      const benchmarkValues = benchmarkData.value.map(p => p.value)
      const isEmpty = dates.length === 0

      const isDarkMode = isDark.value
      const axisColor   = isDarkMode ? 'rgba(255,255,255,0.2)' : '#e5e5e5'
      const labelColor  = isDarkMode ? 'rgba(255,255,255,0.45)' : '#999'
      const tooltipBg   = isDarkMode ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.95)'
      const tooltipText = isDarkMode ? '#fff' : '#333'

      const riseColor = preferGreenUp.value ? '#52c41a' : '#ff4d4f'
      const fallColor = preferGreenUp.value ? '#ff4d4f' : '#52c41a'
      const riseColorArea = preferGreenUp.value
        ? (isDarkMode ? 'rgba(82,196,26,0.3)' : 'rgba(82,196,26,0.25)')
        : (isDarkMode ? 'rgba(255,77,79,0.3)' : 'rgba(255,77,79,0.25)')
      const riseColorAreaMid = preferGreenUp.value
        ? (isDarkMode ? 'rgba(82,196,26,0.15)' : 'rgba(82,196,26,0.1)')
        : (isDarkMode ? 'rgba(255,77,79,0.15)' : 'rgba(255,77,79,0.1)')
      const fallColorArea = preferGreenUp.value
        ? (isDarkMode ? 'rgba(255,77,79,0.3)' : 'rgba(255,77,79,0.25)')
        : (isDarkMode ? 'rgba(82,196,26,0.3)' : 'rgba(82,196,26,0.25)')
      const fallColorAreaMid = preferGreenUp.value
        ? (isDarkMode ? 'rgba(255,77,79,0.15)' : 'rgba(255,77,79,0.1)')
        : (isDarkMode ? 'rgba(82,196,26,0.15)' : 'rgba(82,196,26,0.1)')

      const lastStrategyVal = strategyValues.length > 0 ? strategyValues[strategyValues.length - 1] : 0
      const firstStrategyVal = strategyValues.length > 0 ? strategyValues[0] : 0
      const flatColor = isDarkMode ? 'rgba(255,255,255,0.85)' : 'rgba(30,30,30,0.85)'
      const flatColorArea = isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(30,30,30,0.12)'
      const flatColorAreaMid = isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(30,30,30,0.05)'
      const diff = lastStrategyVal - firstStrategyVal
      const isUptrend = diff > 0
      const isFlat    = diff === 0
      const strategyLineColor = isFlat ? flatColor : isUptrend ? riseColor : fallColor

      const strategyLabel = strategySeriesName.value
      const benchmarkLabel = benchmarkName.value

      const option: echarts.EChartsOption = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: tooltipBg,
          borderColor: isDarkMode ? '#333' : '#e0e0e0',
          textStyle: { color: tooltipText, fontSize: 12 },
          axisPointer: { type: 'cross', crossStyle: { color: labelColor } },
          formatter: (params: any) => {
            if (!Array.isArray(params)) return ''
            let html = `<div style="font-size:12px;"><div style="margin-bottom:4px;">${params[0].axisValue}</div>`
            params.forEach((p: any) => {
              const isStrategy = p.seriesName === SERIES_STRATEGY
              const displayName = isStrategy ? strategyLabel : benchmarkLabel
              const valueColor = p.value > 0 ? riseColor : p.value < 0 ? fallColor : 'inherit'
              const markerColor = isStrategy
                ? strategyLineColor
                : (isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(100,120,160,0.7)')
              const marker = `<span style="display:inline-block;margin-right:4px;border-radius:50%;width:10px;height:10px;background:${markerColor};"></span>`
              const sign = p.value > 0 ? '+' : ''
              html += `<div>${marker} ${displayName}: <span style="color:${valueColor};font-weight:600;">${sign}${Number(p.value).toFixed(2)}%</span></div>`
            })
            html += '</div>'
            return html
          }
        },
        legend: { show: false },
        grid: { left: '1%', right: 50, bottom: '6%', top: '4%', containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLine: { show: !isEmpty, lineStyle: { color: axisColor } },
          axisTick: { show: false },
          axisLabel: {
            show: !isEmpty,
            color: labelColor,
            fontSize: 11,
            interval: (index: number) => {
              const total = dates.length
              if (total === 0) return false
              const midIndex = Math.floor((total - 1) / 2)
              return index === 0 || index === total - 1 || index === midIndex
            },
            showMinLabel: true,
            showMaxLabel: true,
            formatter: (value: string) => value
          }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: !isEmpty, lineStyle: { color: axisColor, type: 'dashed' } },
          axisLabel: { show: !isEmpty, color: labelColor, fontSize: 11, formatter: '{value}%' }
        },
        series: [
          {
            name: SERIES_STRATEGY,
            type: 'line',
            data: strategyValues,
            smooth: true,
            symbol: 'none',
            lineStyle: { color: strategyLineColor, width: 2 },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0,   color: isFlat ? flatColorArea    : isUptrend ? riseColorArea    : fallColorArea    },
                { offset: 0.5, color: isFlat ? flatColorAreaMid : isUptrend ? riseColorAreaMid : fallColorAreaMid },
                { offset: 1,   color: 'rgba(0,0,0,0)' }
              ])
            }
          },
          {
            name: SERIES_BENCHMARK,
            type: 'line',
            data: benchmarkValues,
            smooth: true,
            symbol: 'none',
            lineStyle: {
              color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(100,120,160,0.7)',
              width: 1.5
            }
          }
        ]
      }

      chartInstance.clear()
      chartInstance.setOption(option, { notMerge: true })
    }

    const handleResize = () => { chartInstance?.resize() }

    // code 变化时重新拉数据（切换策略）；无 code 时清空并展示空状态
    watch(() => props.code, () => {
      fetchData()
    })

    watch(isDark, async () => {
      await nextTick()
      renderChart()
    })

    watch(isTraditionalVal, async () => {
      await nextTick()
      if (strategyData.value.length > 0) renderChart()
      switchLanguage()
    })

    onMounted(() => {
      window.addEventListener('resize', handleResize)
      switchLanguage()
      fetchData()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
      chartInstance?.dispose()
    })

    const hasData = computed(() => strategyData.value.length > 0)

    const legendStrategyColor = computed(() => {
      const riseC = preferGreenUp.value ? '#52c41a' : '#ff4d4f'
      const fallC = preferGreenUp.value ? '#ff4d4f' : '#52c41a'
      const flatC = isDark.value ? 'rgba(255,255,255,0.85)' : 'rgba(30,30,30,0.85)'
      if (strategyData.value.length < 2) return riseC
      const first = strategyData.value[0].value
      const last = strategyData.value[strategyData.value.length - 1].value
      if (last === first) return flatC
      return last > first ? riseC : fallC
    })

    // 图例末尾数值
    const formatLegendVal = (val: number) => {
      const sign = val > 0 ? '+' : ''
      return `${sign}${val.toFixed(2)}%`
    }

    const getPctClass = (val: number) => {
      if (val > 0) return preferGreenUp.value ? 'legend-up-green' : 'legend-up-red'
      if (val < 0) return preferGreenUp.value ? 'legend-down-red' : 'legend-down-green'
      return 'legend-flat'
    }

    const legendStrategyValue = computed(() => {
      const d = strategyData.value
      if (!d.length) return ''
      return formatLegendVal(d[d.length - 1].value)
    })

    const legendStrategyValueClass = computed(() => {
      const d = strategyData.value
      if (!d.length) return ''
      return getPctClass(d[d.length - 1].value)
    })

    const legendBenchmarkValue = computed(() => {
      const d = benchmarkData.value
      if (!d.length) return ''
      return formatLegendVal(d[d.length - 1].value)
    })

    const legendBenchmarkValueClass = computed(() => {
      const d = benchmarkData.value
      if (!d.length) return ''
      return getPctClass(d[d.length - 1].value)
    })

    return {
      chartRef,
      chartVars,
      tran,
      isDark,
      currentRange,
      timeOptions,
      benchmarkName,
      loading,
      hasData,
      changeRange,
      legendStrategyColor,
      legendStrategyValue,
      legendStrategyValueClass,
      legendBenchmarkValue,
      legendBenchmarkValueClass,
    }
  }
})
